
import { Button } from "@/components/ui/button";
import { ImagePlus, Plus, X } from "lucide-react";
import { useRef, useState } from "react";
import { useR2Upload } from "@/hooks/use-r2-upload";
import { toast } from "sonner";

export interface EpisodeImagesUploaderProps {
  onImagesUpload: (urls: string[]) => void;
  episodeImages: string[];
  disabled?: boolean;
  userId?: string;
}

export function EpisodeImagesUploader({ 
  onImagesUpload, 
  episodeImages,
  disabled = false,
  userId
}: EpisodeImagesUploaderProps) {
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Create a Promise-returning onUpload function for useR2Upload
  const handleUploadComplete = async (url: string): Promise<void> => {
    return Promise.resolve();
  };
  
  const { handleUpload } = useR2Upload({ 
    folder: 'episodes',
    userId: userId || '',
    onUpload: handleUploadComplete
  });

  const handleAddImage = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  // Image compression function
  const compressImage = (file: File, maxWidth = 1600): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions if image is too large
          if (width > maxWidth) {
            height = Math.round(height * (maxWidth / width));
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);
          
          // Use better format when possible
          const format = file.type === 'image/png' ? 'image/webp' : file.type;
          const quality = 0.8;
          
          canvas.toBlob((blob) => {
            if (blob) {
              const newFile = new File([blob], file.name.split('.')[0] + (format === 'image/webp' ? '.webp' : '.' + format.split('/')[1]), {
                type: format
              });
              resolve(newFile);
            } else {
              reject(new Error('Canvas to Blob conversion failed'));
            }
          }, format, quality);
        };
        img.onerror = () => reject(new Error('Image loading failed'));
      };
      reader.onerror = () => reject(new Error('FileReader failed'));
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0 || !userId) {
      return;
    }

    try {
      setUploadingIndex(episodeImages.length);
      
      const newUrls: string[] = [];
      const files = Array.from(e.target.files);
      const totalFiles = files.length;
      
      // Create a batch of promises to upload all files
      const uploadPromises = files.map(async (file, i) => {
        setUploadingIndex(episodeImages.length + i);
        
        // Check file size
        if (file.size > 10 * 1024 * 1024) { // > 10MB
          toast.error(`Arquivo "${file.name}" muito grande. Será ignorado.`);
          return null;
        }
        
        // Try compressing the image if it's large
        let fileToUpload = file;
        if (file.type.startsWith('image/') && file.size > 1 * 1024 * 1024) {
          try {
            const compressedFile = await compressImage(file);
            // Only use compressed version if it's actually smaller
            if (compressedFile.size < file.size) {
              fileToUpload = compressedFile;
            }
          } catch (error) {
            console.error(`Error compressing image ${file.name}:`, error);
            // Use original if compression fails
          }
        }
        
        const url = await handleUpload(fileToUpload);
        return url || null;
      });
      
      // Wait for all uploads to complete
      const results = await Promise.all(uploadPromises);
      
      // Filter out any failed uploads
      const successfulUrls = results.filter(url => url !== null) as string[];
      newUrls.push(...successfulUrls);
      
      if (newUrls.length > 0) {
        onImagesUpload([...episodeImages, ...newUrls]);
        toast.success(`${newUrls.length} de ${totalFiles} imagens enviadas com sucesso`);
      } else {
        toast.error("Falha ao enviar imagens");
      }
    } catch (error) {
      console.error("Error uploading episode images:", error);
      toast.error("Falha ao enviar imagens");
    } finally {
      setUploadingIndex(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    const newImages = [...episodeImages];
    newImages.splice(index, 1);
    onImagesUpload(newImages);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4">
        {episodeImages.map((url, index) => (
          <div key={index} className="relative">
            <img 
              src={url} 
              alt={`Episode image ${index + 1}`}
              className="h-24 w-24 rounded-md object-cover"
              width={96}
              height={96}
              loading="lazy"
            />
            <button
              type="button"
              onClick={() => handleRemoveImage(index)}
              className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-destructive-foreground"
              disabled={disabled}
              aria-label={`Remove image ${index + 1}`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
        {uploadingIndex !== null && (
          <div className="flex h-24 w-24 items-center justify-center rounded-md border border-dashed border-border bg-muted">
            <div className="flex flex-col items-center justify-center text-sm text-muted-foreground">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-primary"></div>
              <span className="mt-2">Enviando</span>
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={handleAddImage}
          className="flex h-24 w-24 flex-col items-center justify-center rounded-md border border-dashed border-border bg-muted hover:bg-muted/80"
          disabled={disabled}
          aria-label="Add images"
        >
          <ImagePlus className="mb-1 h-8 w-8 text-muted-foreground" aria-hidden="true" />
          <span className="text-xs text-muted-foreground">Adicionar imagens</span>
        </button>
      </div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept="image/*"
        multiple
        disabled={disabled}
        aria-label="Upload multiple images"
      />
      <div className="flex items-center">
        <Button 
          type="button" 
          onClick={handleAddImage}
          disabled={disabled}
          variant="outline"
          size="sm"
          className="gap-1"
        >
          <Plus className="h-4 w-4" />
          Adicionar mais imagens
        </Button>
      </div>
    </div>
  );
}
